// DOCS: https://docs.amplify.aws/lib/restapi/getting-started/q/platform/js/#automated-setup-create-new-rest-api
import { API as AmplifyApi, Auth } from "aws-amplify";
import { IAlertInfo } from "@/types";
import { ErrorInfo } from "@/types/swagger";
import _ from "lodash";
import Utils from "@/common/utils";

const API_TIMEOUT_MILLISECOND = 30 * 1000; // ms

const API_ERROR_CODE = {
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  CLIENT_NETWORK_ERROR: "CLIENT_NETWORK_ERROR",
  SERVER_NETWORK_ERROR: "SERVER_NETWORK_ERROR",
  HTTP_STATUS_CODE_ERROR: "HTTP_STATUS_CODE_ERROR",
  RESPONSE_STATUS_ERROR: "RESPONSE_STATUS_ERROR",
};

const API_ENV = "dev";
const API_ENDPOINT = {
  dev: "https://dev.api.fsm.timetapps.jp",
  prod: "https://www.api.fsm.timetapps.jp",
};
const API_KEY = {
  dev: "05GwVkaCyD7vI1yMkULiO4I94NbAcNCXRqT8S386",
  prod: "",
};

export default class API {
  /**
   * API関連クラスの初期化処理
   *
   * @name init
   * @function
   */
  async init() {
    const user = await Auth.currentAuthenticatedUser();
    const token = user.signInUserSession.idToken.jwtToken;
    AmplifyApi.configure({
      endpoints: [
        {
          name: API_ENV,
          endpoint: API_ENDPOINT[API_ENV],
          region: "ap-northeast-1",
          custom_header: () => ({ Authorization: token }),
        },
      ],
    });
  }

  nonAuthInit() {
    AmplifyApi.configure({
      endpoints: [
        {
          name: API_ENV,
          endpoint: API_ENDPOINT[API_ENV],
          region: "ap-northeast-1",
          custom_header: () => ({ "x-api-key": API_KEY[API_ENV] }),
        },
      ],
    });
  }

  commonRequestHeader() {
    const ret: any = {};
    const url = window.location.href;
    ret["x-client-url"] = encodeURI(url);
    return ret;
  }

  getAlertInfoFromApiErrorInfo(error: ErrorInfo | any): IAlertInfo | null {
    if (error.error_message && error.item && error.error_code) {
      const mes = Utils.getErrorMessageByErrorCode(error.error_code);
      return {
        message: mes ? mes : error.error_message,
        item: error.item,
        code: API_ERROR_CODE.RESPONSE_STATUS_ERROR,
      };
    } else {
      const alert = Utils.getCommonAlertInfo();
      alert.code = API_ERROR_CODE.RESPONSE_STATUS_ERROR;
      return alert;
    }
  }

  checkResponse(response: any) {
    const API_STATUS_SUCCESS = 0;
    if (
      response &&
      !_.isNil(response.state) &&
      response.state !== API_STATUS_SUCCESS
    ) {
      if (response.error_info && Array.isArray(response.error_info)) {
        const alertInfos: IAlertInfo[] = [];
        response.error_info.forEach(async (error: any) => {
          if (error.error_code === "EG0001") {
            await Utils.logout();
            return null;
          } else {
            const alertInfo: IAlertInfo | null =
              this.getAlertInfoFromApiErrorInfo(error);
            if (alertInfo) {
              alertInfos.push(alertInfo);
            }
          }
        });

        throw alertInfos;
      }
    }
  }

  /**
   * APIのエラーをチェックし、各エラー時の動作を行う
   *
   * @name checkErrorAndAction
   * @function
   * @param {Object} error - エラーオブジェクト
   * @return {Object} - レスポンス
   */
  async checkErrorAndAction(
    error: IAlertInfo[] | any
  ): Promise<IAlertInfo[] | null> {
    if (
      Array.isArray(error) &&
      error.length > 0 &&
      error[0].code === API_ERROR_CODE.RESPONSE_STATUS_ERROR
    ) {
      // APIのレスポンスに含まれるステータス「0（正常）」以外
      return error;
    } else if (error.code === API_ERROR_CODE.RESPONSE_STATUS_ERROR) {
      // APIのレスポンスに含まれるステータス「0（正常）」以外
      if (error.message) {
        return [error];
      } else {
        return [Utils.getCommonAlertInfo()];
      }
    } else if (error.code === "ECONNABORTED") {
      // タイムアウトエラーによるAPI実行失敗時
      const ret: IAlertInfo = {
        item: "TIMEOUT_ERROR",
        message: Utils.getErrorMessageByErrorCode("TIMEOUT_ERROR"),
      };
      return [ret];
    } else if (error.message === "Network Error") {
      // ネットワークエラーによるAPI実行失敗
      const ret: IAlertInfo = {
        item: "NETWORK_ERROR",
        message: Utils.getErrorMessageByErrorCode("NETWORK_ERROR"),
      };
      return [ret];
    } else if (error === "The user is not authenticated") {
      await Utils.logout();
      return null;
    } else {
      const ret: IAlertInfo = {
        item: "HTTP_STATUS_CODE_ERROR",
        message: Utils.getErrorMessageByErrorCode("HTTP_STATUS_CODE_ERROR"),
      };
      return [ret];
    }
  }

  async get(path: string, query: any) {
    try {
      await this.init();
      const ex = {
        headers: this.commonRequestHeader(),
        queryStringParameters: query,
        timeout: API_TIMEOUT_MILLISECOND,
      };
      const res = await AmplifyApi.get("dev", path, ex);
      this.checkResponse(res);
      return res;
    } catch (e) {
      console.error(e);
      const error = await this.checkErrorAndAction(e);
      throw error;
    }
  }

  /**
   * POSTリクエスト
   *
   * @name post
   * @function
   * @param {String} path - パス
   * @param {Object} init - 初期化属性、オプション
   * @return {Object} - レスポンス
   */
  async post(path: string, init: any, isAuth = true) {
    try {
      if (isAuth) {
        await this.init();
      } else {
        this.nonAuthInit();
      }
      const ex = init;
      ex.headers = this.commonRequestHeader();
      ex.timeout = API_TIMEOUT_MILLISECOND;
      const res = await AmplifyApi.post("dev", path, ex);
      this.checkResponse(res);
      return res;
    } catch (e) {
      console.error(e);
      const error = await this.checkErrorAndAction(e);
      throw error;
    }
  }

  /**
   * PUTリクエスト
   *
   * @name post
   * @function
   * @param {String} path - パス
   * @param {Object} init - 初期化属性、オプション
   * @return {Object} - レスポンス
   */
  async put(path: string, init: any) {
    try {
      await this.init();
      const ex = init;
      ex.headers = this.commonRequestHeader();
      ex.timeout = API_TIMEOUT_MILLISECOND;
      const res = await AmplifyApi.put("dev", path, ex);
      this.checkResponse(res);
      return res;
    } catch (e) {
      console.error(e);
      const error = await this.checkErrorAndAction(e);
      throw error;
    }
  }
}
